import { DateTime } from "luxon";
import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faCheck,
  faCreditCard,
  faFileInvoiceDollar
} from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import { Button } from "@mantine/core";
import InvoiceApi from "../../data/api/invoiceApi";
import { IState } from "../../data/reducers/state";
import { IAssociation } from "../../types/association/Association";
import { IInvoice } from "../../types/invoice/Invoice";
import { toDateTime } from "../../util/date+helper";
import { AdminPageLayout, AdminTable } from "../admin/common";

interface IInvoiceDashboardProps {
  associations: { [id: string]: IAssociation };
  // Optional prop to filter to specific association
  selectedAssociation?: IAssociation | null;
}

const InvoiceDashboard: React.FC<IInvoiceDashboardProps> = ({
  associations,
  selectedAssociation,
}) => {
  const [invoices, setInvoices] = React.useState<IInvoice[] | null>();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>();

  function getInvoices() {
    InvoiceApi.getInvoices()
      .then((data) => {
        setInvoices(data);
        setLoading(false);
      })
      .catch((er) => {
        setLoading(false);
        setError(er.message);
      });
  }

  React.useEffect(getInvoices, []);

  const filteredInvoices = invoices?.filter(invoice => 
    selectedAssociation ? invoice.association === selectedAssociation._id : true
  ) || [];

  const headers = [
    'Invoice Number',
    ...(selectedAssociation ? [] : ['Association']),
    'Invoice Month', 
    'Amount', 
    'Payment Status'
  ];

  return (
    <AdminPageLayout
      title="Invoices"
      description="Manage and track invoice payments across your associations"
      icon={faFileInvoiceDollar}
    >
      <AdminTable
        headers={headers}
        loading={loading}
        error={error}
        isEmpty={filteredInvoices.length === 0}
        emptyMessage={selectedAssociation 
          ? `No invoices found for ${selectedAssociation.name}` 
          : "No invoices found"
        }
      >
        {filteredInvoices.map((invoice) => (
          <tr key={invoice._id}>
            <td>
              <a
                href={`${InvoiceApi.URL + invoice._id}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="text-primary fw-medium"
              >
                <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                {invoice._id}
              </a>
            </td>
            {!selectedAssociation && (
              <td>
                <span className="badge bg-secondary">
                  {associations[invoice.association]?.name}
                </span>
              </td>
            )}
            <td>
              {DateTime.now()
                .set({ month: invoice.month, year: invoice.year })
                .toFormat("LLLL, yyyy")}
            </td>
            <td>
              <span className="fw-bold text-success">${invoice.cost}</span>
            </td>
            <td>
              {invoice.paid ? (
                <div>
                  <span className="badge bg-success me-2">
                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                    Paid
                  </span>
                  <small className="text-muted">
                    {toDateTime(invoice.paid)?.toLocaleString(DateTime.DATETIME_MED)}
                  </small>
                </div>
              ) : (
                <Button
                  color="yellow"
                  size="sm"
                  onClick={() =>
                    InvoiceApi.updateInvoice(invoice._id, {
                      paid: new Date(),
                    })
                      .then(getInvoices)
                      .catch((er) => setError(er.message))
                  }
                >
                  <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                  Mark as Paid
                </Button>
              )}
            </td>
          </tr>
        ))}
      </AdminTable>
    </AdminPageLayout>
  );
};

const mapStateToProps = (state: IState, props: any) => ({
  associations: state.associations.map,
});

export default connect(mapStateToProps)(InvoiceDashboard);
